import React, {
  createContext,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useState,
} from "react";
import { HttpClient } from "../lib/http/HttpClient";
import { AuthClient } from "../lib/auth/AuthClient";
import { ApiClient } from "../lib/api/ApiClient";
import { MemoryTokenStore } from "../lib/auth/TokenStore";

export const SecurityContext = createContext({
  auth: null,
  api: null,
  user: null,
  loading: true,
  login: null,
  acceptInvitation: null,
  logout: null,
  reload: null,
  setUser: null,
  isAuthenticated: false,
});

export function SecurityProvider({
  children,
  apiBaseURL,
  authBasePath = "/auth",
  apiBasePath = "/api",
}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clients = useMemo(() => {
    const tokenStore = new MemoryTokenStore(); // access token en memoria

    const http = new HttpClient({
      baseURL: apiBaseURL,
      credentials: "include", // para enviar cookie refresh_token en /auth/refresh
      getAccessToken: () => tokenStore.get()?.accessToken,
    });

    const auth = new AuthClient({ http, tokenStore, basePath: authBasePath });
    const api = new ApiClient({
      http,
      tokenStore,
      auth,
      basePath: apiBasePath,
    });
    return {
      auth,
      api,
      getAccessToken: () => tokenStore.get()?.accessToken || null,
    };
  }, [apiBaseURL, authBasePath, apiBasePath]);

  const reload = useCallback(async () => {
    setLoading(true);

    try {
      const currentUser = await clients.auth.me().catch(() => null);
      setUser(currentUser);
      return currentUser;
    } finally {
      setLoading(false);
    }
  }, [clients.auth]);

  const login = useCallback(
    async (email, password) => {
      await clients.auth.login({ email, password });

      const loggedUser = await clients.auth.me();

      if (!loggedUser) {
        throw new Error("No se pudo obtener el usuario autenticado");
      }

      setUser(loggedUser);
      return loggedUser;
    },
    [clients.auth],
  );

  const acceptInvitation = useCallback(
    async (data) => {
      const res = await clients.auth.acceptInvitation(data);
      return res;
    },
    [clients.auth],
  );

  const logout = useCallback(async () => {
    await clients.auth.logout();
    setUser(null);
  }, [clients.auth]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);

        // 1. Intentamos refrescar el token (usa la cookie del navegador)
        await clients.auth.refresh();

        // 2. Si funcionó, pedimos los datos del usuario
        const userData = await clients.auth.me();
        setUser(userData);
      } catch (error) {
        // Si falla (token expirado o no existe), no hacemos nada, el usuario queda anónimo
        console.log("Sesión no recuperada, usuario anónimo");
        setUser(null);
      } finally {
        // 3. Ya terminamos de verificar, podemos mostrar la app
        setLoading(false);
      }
    };

    initAuth();
  }, [clients.auth]); // Se ejecuta al montar

  const value = useMemo(
    () => ({
      ...clients,
      user,
      loading,
      login,
      acceptInvitation,
      logout,
      reload,
      setUser,
      isAuthenticated: Boolean(user),
    }),
    [clients, loading, login, logout, reload, user],
  );

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

/**
 * Hook personalizado para acceder a 'auth' y 'api' rápidamente.
 */
export const useSecurity = () => {
  const context = useContext(SecurityContext);

  if (!context) {
    throw new Error("useSecurity debe ser usado dentro de un SecurityProvider");
  }

  return context;
};